import { useState, useCallback, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Plus, Trash2, ArrowRight, Loader2, RefreshCcw, TrendingUp, TrendingDown, Bookmark, Bell } from "lucide-react";
import { toast } from "sonner";
import SavedScenariosList from "@/components/SavedScenariosList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_SCENARIO = () => ({
  _uid: `scn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  type: 'Sell',
  symbol: '',
  asset_class_id: '',
  quantity: '',
  price: '',
  brokerage: '0',
  taxes: '0',
  other_charges: '0',
});

function DeltaBadge({ value, currency }) {
  const positive = value >= 0;
  const tone = positive ? 'text-success' : 'text-destructive';
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-semibold ${tone}`}>
      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
      {positive ? '+' : ''}{currency} {value.toFixed(2)}
    </span>
  );
}

function MetricRow({ label, before, after, delta, currency }) {
  return (
    <div className="grid grid-cols-12 items-center gap-2 py-2 border-b border-border last:border-b-0">
      <div className="col-span-4 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {label}
      </div>
      <div className="col-span-3 text-sm tabular-nums text-right text-muted-foreground">
        {currency} {before.toFixed(2)}
      </div>
      <div className="col-span-1 text-center text-muted-foreground">
        <ArrowRight className="h-3.5 w-3.5 inline" />
      </div>
      <div className="col-span-2 text-sm tabular-nums text-right font-semibold">
        {currency} {after.toFixed(2)}
      </div>
      <div className="col-span-2 text-right">
        <DeltaBadge value={delta} currency={currency} />
      </div>
    </div>
  );
}

function WhatIfCalculator({ open, onOpenChange, assetClasses, holdings, currency }) {
  const [activeTab, setActiveTab] = useState('build');
  const [scenarios, setScenarios] = useState([EMPTY_SCENARIO()]);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [saveForm, setSaveForm] = useState({ name: '', notes: '', watch_enabled: false });
  const [saving, setSaving] = useState(false);

  const fetchSaved = useCallback(async () => {
    try {
      const res = await fetch(`${API}/scenarios?currency=${currency}`, { credentials: 'include' });
      if (res.ok) setSavedScenarios(await res.json());
    } catch (err) {
      console.error('fetchSaved failed:', err);
    }
  }, [currency]);

  useEffect(() => {
    if (open) fetchSaved();
  }, [open, fetchSaved]);

  const updateScenario = (idx, field, value) => {
    setScenarios((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addScenario = () => {
    if (scenarios.length >= 10) {
      toast.warning('Maximum 10 scenarios');
      return;
    }
    setScenarios((prev) => [...prev, EMPTY_SCENARIO()]);
  };

  const removeScenario = (idx) => {
    setScenarios((prev) => prev.filter((_, i) => i !== idx));
  };

  const resetAll = () => {
    setScenarios([EMPTY_SCENARIO()]);
    setResult(null);
    setSaveForm({ name: '', notes: '', watch_enabled: false });
  };

  const loadSavedResult = (data) => {
    // Hydrate the form from a saved scenario run
    const hydrated = data.scenarios.map((s, i) => ({
      _uid: `scn_loaded_${Date.now()}_${i}`,
      type: s.type,
      symbol: s.symbol,
      asset_class_id: s.asset_class_id,
      quantity: String(s.quantity),
      price: String(s.price),
      brokerage: String(s.brokerage ?? 0),
      taxes: String(s.taxes ?? 0),
      other_charges: String(s.other_charges ?? 0),
    }));
    setScenarios(hydrated);
    setResult(data);
    if (data.saved_scenario) {
      setSaveForm({
        name: data.saved_scenario.name,
        notes: data.saved_scenario.notes || '',
        watch_enabled: data.saved_scenario.watch_enabled
      });
    }
    setActiveTab('build');
  };

  const handleSave = async () => {
    if (!saveForm.name.trim()) {
      toast.error('Please give the scenario a name');
      return;
    }
    if (!result) {
      toast.error('Calculate a scenario first');
      return;
    }
    // Re-build the payload from current form state (use cleaned numbers from result.scenarios)
    setSaving(true);
    try {
      const res = await fetch(`${API}/scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: saveForm.name.trim(),
          notes: saveForm.notes.trim() || null,
          currency,
          scenarios: result.scenarios,
          watch_enabled: saveForm.watch_enabled,
        }),
      });
      if (res.ok) {
        toast.success('Scenario saved');
        setSaveForm({ name: '', notes: '', watch_enabled: false });
        fetchSaved();
      } else {
        let detail = null;
        try { detail = JSON.parse(await res.text()).detail; } catch { /* ignore */ }
        toast.error(typeof detail === 'string' ? detail : 'Failed to save scenario');
      }
    } catch (err) {
      console.error('save scenario failed:', err);
      toast.error('Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleCalculate = async () => {
    // Validate
    const cleaned = [];
    for (let i = 0; i < scenarios.length; i++) {
      const s = scenarios[i];
      if (!s.symbol || !s.asset_class_id || !s.quantity || !s.price) {
        toast.error(`Scenario ${i + 1}: fill symbol, class, quantity and price`);
        return;
      }
      const qty = parseFloat(s.quantity);
      const price = parseFloat(s.price);
      if (!(qty > 0) || !(price > 0)) {
        toast.error(`Scenario ${i + 1}: quantity and price must be > 0`);
        return;
      }
      // Client-side oversell check: better UX, no wasted network call
      if (s.type === 'Sell') {
        const sym = s.symbol.trim().toUpperCase();
        const h = holdings.find(x => x.symbol === sym && x.asset_class_id === s.asset_class_id);
        const available = h?.total_quantity || 0;
        if (qty > available) {
          toast.error(
            available > 0
              ? `Scenario ${i + 1}: cannot sell ${qty} of ${sym} — only ${available} available`
              : `Scenario ${i + 1}: no existing holding for ${sym}`
          );
          return;
        }
      }
      cleaned.push({
        type: s.type,
        symbol: s.symbol.trim().toUpperCase(),
        asset_class_id: s.asset_class_id,
        quantity: qty,
        price: price,
        brokerage: parseFloat(s.brokerage) || 0,
        taxes: parseFloat(s.taxes) || 0,
        other_charges: parseFloat(s.other_charges) || 0,
      });
    }

    setCalculating(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/investments/what-if`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currency, scenarios: cleaned, include_ai_explanation: true })
      });
      if (res.ok) {
        setResult(await res.json());
      } else {
        // Best-effort: try to read JSON detail, but fall back to status-based message
        // (preview environments can pre-consume the response body)
        let detail = null;
        try {
          const text = await res.text();
          detail = JSON.parse(text).detail;
        } catch {
          // body unavailable - use status-based fallback
        }
        let msg;
        if (typeof detail === 'string') {
          msg = detail;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          msg = detail[0].msg;
        } else if (res.status === 400) {
          msg = 'Invalid scenario — check quantities against your current holdings';
        } else if (res.status === 422) {
          msg = 'Validation failed — check your inputs';
        } else if (res.status === 401) {
          msg = 'Session expired. Please sign in again.';
        } else {
          msg = `Calculation failed (HTTP ${res.status})`;
        }
        toast.error(msg);
      }
    } catch (err) {
      console.error('what-if calc failed:', err);
      toast.error('Network error — please try again');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="whatif-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-xl sm:text-2xl tracking-tight font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" strokeWidth={1.5} />
            What-If Scenarios
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Simulate Buy / Sell decisions and see the P&amp;L and allocation impact without committing. GPT-5.2 explains the outcome in plain English.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 py-5">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList data-testid="whatif-tabs" className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger data-testid="tab-build" value="build">Build</TabsTrigger>
              <TabsTrigger data-testid="tab-saved" value="saved">
                Saved
                {savedScenarios.length > 0 && (
                  <Badge variant="outline" className="ml-2 h-5 text-[10px]">
                    {savedScenarios.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="saved" className="space-y-3 mt-0">
              <SavedScenariosList
                savedScenarios={savedScenarios}
                assetClasses={assetClasses}
                currency={currency}
                onRefresh={fetchSaved}
                onRun={loadSavedResult}
              />
            </TabsContent>

            <TabsContent value="build" className="space-y-5 mt-0">
          {/* Scenario Builder */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Scenarios ({scenarios.length}/10)
              </p>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="whatif-reset"
                  variant="ghost"
                  size="sm"
                  onClick={resetAll}
                  className="h-8"
                >
                  <RefreshCcw className="mr-1 h-3.5 w-3.5" />
                  Reset
                </Button>
                <Button
                  data-testid="whatif-add-scenario"
                  variant="outline"
                  size="sm"
                  onClick={addScenario}
                  className="h-8"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add
                </Button>
              </div>
            </div>

            {scenarios.map((s, idx) => (
              <Card
                key={s._uid || idx}
                data-testid={`whatif-scenario-${idx}`}
                className="border border-border"
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="text-xs">#{idx + 1}</Badge>
                    {scenarios.length > 1 && (
                      <Button
                        data-testid={`whatif-remove-${idx}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => removeScenario(idx)}
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  <ToggleGroup
                    type="single"
                    value={s.type}
                    onValueChange={(v) => v && updateScenario(idx, 'type', v)}
                    className="grid grid-cols-2 gap-2"
                  >
                    <ToggleGroupItem
                      data-testid={`whatif-buy-${idx}`}
                      value="Buy"
                      className="data-[state=on]:bg-success data-[state=on]:text-success-foreground border border-border h-9"
                    >
                      Buy
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      data-testid={`whatif-sell-${idx}`}
                      value="Sell"
                      className="data-[state=on]:bg-destructive data-[state=on]:text-destructive-foreground border border-border h-9"
                    >
                      Sell
                    </ToggleGroupItem>
                  </ToggleGroup>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Asset Class</Label>
                      <Select
                        value={s.asset_class_id}
                        onValueChange={(v) => updateScenario(idx, 'asset_class_id', v)}
                      >
                        <SelectTrigger data-testid={`whatif-class-${idx}`} className="h-9">
                          <SelectValue placeholder="Class" />
                        </SelectTrigger>
                        <SelectContent>
                          {assetClasses.map((a) => (
                            <SelectItem key={a.asset_class_id} value={a.asset_class_id}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Symbol</Label>
                      <Input
                        data-testid={`whatif-symbol-${idx}`}
                        placeholder="AAPL"
                        value={s.symbol}
                        onChange={(e) => updateScenario(idx, 'symbol', e.target.value.toUpperCase())}
                        className="h-9"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        data-testid={`whatif-qty-${idx}`}
                        type="number"
                        step="any"
                        min="0"
                        value={s.quantity}
                        onChange={(e) => updateScenario(idx, 'quantity', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{s.type} Price ({currency})</Label>
                      <Input
                        data-testid={`whatif-price-${idx}`}
                        type="number"
                        step="any"
                        min="0"
                        value={s.price}
                        onChange={(e) => updateScenario(idx, 'price', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Brokerage</Label>
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={s.brokerage}
                        onChange={(e) => updateScenario(idx, 'brokerage', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Taxes</Label>
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={s.taxes}
                        onChange={(e) => updateScenario(idx, 'taxes', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Other</Label>
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={s.other_charges}
                        onChange={(e) => updateScenario(idx, 'other_charges', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>

                  {s.type === 'Sell' && s.symbol && s.asset_class_id && (() => {
                    const h = holdings.find(
                      x => x.symbol === s.symbol.trim().toUpperCase() && x.asset_class_id === s.asset_class_id
                    );
                    if (h) {
                      return (
                        <p className="text-xs text-muted-foreground">
                          Available: {h.total_quantity} · Avg: {currency} {h.avg_buy_price.toFixed(2)}
                        </p>
                      );
                    }
                    return (
                      <p className="text-xs text-destructive">
                        No existing holding for {s.symbol.trim().toUpperCase()}
                      </p>
                    );
                  })()}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Calculate button */}
          <Button
            data-testid="whatif-calculate"
            onClick={handleCalculate}
            disabled={calculating}
            className="w-full h-11 transition-transform duration-200 hover:-translate-y-px"
          >
            {calculating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Calculating impact and asking GPT-5.2...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Calculate Impact
              </>
            )}
          </Button>

          {/* Results */}
          {result && (
            <div data-testid="whatif-result" className="space-y-4">
              <Card className="border border-border">
                <CardContent className="p-4 sm:p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">
                    Before → After
                  </p>
                  <MetricRow
                    label="Invested"
                    before={result.before.total_invested}
                    after={result.after.total_invested}
                    delta={result.delta.total_invested}
                    currency={currency}
                  />
                  <MetricRow
                    label="Market Value"
                    before={result.before.total_market_value}
                    after={result.after.total_market_value}
                    delta={result.delta.total_market_value}
                    currency={currency}
                  />
                  <MetricRow
                    label="Unrealized P&L"
                    before={result.before.total_unrealized_pnl}
                    after={result.after.total_unrealized_pnl}
                    delta={result.delta.total_unrealized_pnl}
                    currency={currency}
                  />
                  <MetricRow
                    label="Realized P&L"
                    before={result.before.total_realized_pnl}
                    after={result.after.total_realized_pnl}
                    delta={result.delta.total_realized_pnl}
                    currency={currency}
                  />
                </CardContent>
              </Card>

              {/* Allocation comparison */}
              <Card className="border border-border">
                <CardContent className="p-4 sm:p-5 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Asset Allocation
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Before</p>
                      <div className="space-y-1">
                        {result.before.asset_allocation.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No holdings</p>
                        ) : (
                          result.before.asset_allocation.map((a) => (
                            <div key={a.name} className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{a.name}</span>
                              <span className="tabular-nums">{currency} {a.value.toFixed(0)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">After</p>
                      <div className="space-y-1">
                        {result.after.asset_allocation.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No holdings</p>
                        ) : (
                          result.after.asset_allocation.map((a) => (
                            <div key={a.name} className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{a.name}</span>
                              <span className="tabular-nums font-semibold">{currency} {a.value.toFixed(0)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Explanation */}
              {result.explanation && (
                <Card data-testid="whatif-ai-explanation" className="border border-accent/30 bg-accent/5">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-accent" strokeWidth={1.5} />
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                        GPT-5.2 Analysis
                      </p>
                    </div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {result.explanation}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Save This Scenario */}
              <Card data-testid="whatif-save-form" className="border border-border">
                <CardContent className="p-4 sm:p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Bookmark className="h-4 w-4 text-foreground" strokeWidth={1.8} />
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                      Save & Watch
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="scn-name" className="text-xs">Name *</Label>
                    <Input
                      data-testid="save-scenario-name"
                      id="scn-name"
                      placeholder="e.g. Sell AAPL when it hits $250"
                      value={saveForm.name}
                      onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })}
                      maxLength={120}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="scn-notes" className="text-xs">Notes (Optional)</Label>
                    <Textarea
                      data-testid="save-scenario-notes"
                      id="scn-notes"
                      placeholder="Why this scenario? When to revisit?"
                      value={saveForm.notes}
                      onChange={(e) => setSaveForm({ ...saveForm, notes: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-md border border-border">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 text-accent" strokeWidth={1.8} />
                      <div>
                        <p className="text-sm font-medium">Watch for target</p>
                        <p className="text-xs text-muted-foreground">
                          Alert me when current price crosses any target in this scenario
                        </p>
                      </div>
                    </div>
                    <Switch
                      data-testid="save-scenario-watch"
                      checked={saveForm.watch_enabled}
                      onCheckedChange={(v) => setSaveForm({ ...saveForm, watch_enabled: v })}
                    />
                  </div>
                  <Button
                    data-testid="save-scenario-button"
                    onClick={handleSave}
                    disabled={saving || !saveForm.name.trim()}
                    className="w-full transition-transform duration-200 hover:-translate-y-px"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Bookmark className="mr-2 h-4 w-4" />
                        Save Scenario
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default WhatIfCalculator;
